import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { calculateScore, calculateOptionScores, type ScoringMethod } from '@referee/scoring';
import { useOrganization } from '@referee/graphql-client';
import type { QuestionData } from '@referee/ui';
import { recordScore } from '../stores/trainingStatsStore';

export type TrainingStatus = 'setup' | 'active' | 'finished';

export interface TrainingQuestionResult {
  questionId: string;
  selectedAnswers: string[];
  correctAnswers: string[];
  score: number;
  maxScore: number;
  optionScores?: Record<string, number>;
}

export interface StartTrainingInput {
  catalogId: string;
  questionIds: string[];
  questions: Map<string, QuestionData>;
  correctAnswers: Map<string, string[]>;
  scoringMethod: ScoringMethod;
}

export interface TrainingModeContextValue {
  status: TrainingStatus;
  catalogId: string | null;
  questionIds: string[];
  questions: Map<string, QuestionData>;
  currentIndex: number;
  answers: Record<string, string[]>;
  results: TrainingQuestionResult[];
  totalScore: number;
  maxScore: number;
  scoringMethod: ScoringMethod;
  startTraining: (input: StartTrainingInput) => void;
  setAnswer: (questionId: string, selected: string[]) => void;
  goToQuestion: (index: number) => void;
  finishTraining: () => void;
  resetTraining: () => void;
}

const EMPTY_QUESTIONS: Map<string, QuestionData> = new Map();
const EMPTY_ANSWER_KEYS: Map<string, string[]> = new Map();

const TrainingModeCtx = createContext<TrainingModeContextValue | null>(null);

export function useTrainingMode(): TrainingModeContextValue {
  const ctx = useContext(TrainingModeCtx);
  if (!ctx) {
    throw new Error('useTrainingMode must be used within a TrainingModeProvider');
  }
  return ctx;
}

export function TrainingModeProvider({ children }: { children: ReactNode }) {
  const { organizationId } = useOrganization();

  const [status, setStatus] = useState<TrainingStatus>('setup');
  const [catalogId, setCatalogId] = useState<string | null>(null);
  const [questionIds, setQuestionIds] = useState<string[]>([]);
  const [questions, setQuestions] = useState<Map<string, QuestionData>>(EMPTY_QUESTIONS);
  const [correctAnswers, setCorrectAnswers] = useState<Map<string, string[]>>(EMPTY_ANSWER_KEYS);
  const [scoringMethod, setScoringMethod] = useState<ScoringMethod>('BINARY');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [results, setResults] = useState<TrainingQuestionResult[]>([]);
  const [totalScore, setTotalScore] = useState(0);
  const [maxScore, setMaxScore] = useState(0);

  const startTraining = useCallback((input: StartTrainingInput) => {
    // The caller has already sampled via sampleQuestions() — store the ids exactly as
    // handed in, never resample or reorder them here.
    setCatalogId(input.catalogId);
    setQuestionIds(input.questionIds);
    setQuestions(input.questions);
    setCorrectAnswers(input.correctAnswers);
    setScoringMethod(input.scoringMethod);
    setCurrentIndex(0);
    setAnswers({});
    setResults([]);
    setTotalScore(0);
    setMaxScore(0);
    setStatus('active');
  }, []);

  const setAnswer = useCallback((questionId: string, selected: string[]) => {
    setAnswers((prev) => ({ ...prev, [questionId]: selected }));
  }, []);

  const goToQuestion = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  const finishTraining = useCallback(() => {
    let total = 0;
    let max = 0;
    const computed: TrainingQuestionResult[] = questionIds.map((id) => {
      const options = Object.keys(questions.get(id)?.options ?? {});
      const correct = correctAnswers.get(id) ?? [];
      const selected = answers[id] ?? [];
      const { score, maxScore: questionMaxScore } = calculateScore(
        options,
        correct,
        selected,
        scoringMethod,
      );
      total += score;
      max += questionMaxScore;
      return {
        questionId: id,
        selectedAnswers: selected,
        correctAnswers: correct,
        score,
        maxScore: questionMaxScore,
        ...(scoringMethod !== 'BINARY' && {
          optionScores: calculateOptionScores(options, correct, selected, scoringMethod),
        }),
      };
    });

    setResults(computed);
    setTotalScore(total);
    setMaxScore(max);
    setStatus('finished');

    // Single write site, at the state transition — never on every results render.
    if (catalogId) {
      recordScore(organizationId, catalogId, total, max);
    }
  }, [questionIds, questions, correctAnswers, answers, catalogId, organizationId, scoringMethod]);

  const resetTraining = useCallback(() => {
    setStatus('setup');
    setCatalogId(null);
    setQuestionIds([]);
    setQuestions(EMPTY_QUESTIONS);
    setCorrectAnswers(EMPTY_ANSWER_KEYS);
    setScoringMethod('BINARY');
    setCurrentIndex(0);
    setAnswers({});
    setResults([]);
    setTotalScore(0);
    setMaxScore(0);
  }, []);

  const value = useMemo<TrainingModeContextValue>(
    () => ({
      status,
      catalogId,
      questionIds,
      questions,
      currentIndex,
      answers,
      results,
      totalScore,
      maxScore,
      scoringMethod,
      startTraining,
      setAnswer,
      goToQuestion,
      finishTraining,
      resetTraining,
    }),
    [
      status,
      catalogId,
      questionIds,
      questions,
      currentIndex,
      answers,
      results,
      totalScore,
      maxScore,
      scoringMethod,
      startTraining,
      setAnswer,
      goToQuestion,
      finishTraining,
      resetTraining,
    ],
  );

  return <TrainingModeCtx value={value}>{children}</TrainingModeCtx>;
}
